import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
const PlantBenchmarkingDashboard: React.FC = () => {
  // Plant data
  const plantData = [{
    plant: 'Manesar',
    oee: 76.5,
    scrapRate: 2.1,
    ppm: 295,
    costPerUnit: 142,
    energyPerUnit: 2.8,
    auditScore: 92
  }, {
    plant: 'Chennai',
    oee: 82.0,
    scrapRate: 1.8,
    ppm: 245,
    costPerUnit: 138,
    energyPerUnit: 2.5,
    auditScore: 94
  }, {
    plant: 'Pune',
    oee: 75.0,
    scrapRate: 2.4,
    ppm: 325,
    costPerUnit: 145,
    energyPerUnit: 3.1,
    auditScore: 90
  }, {
    plant: 'Bangalore',
    oee: 79.0,
    scrapRate: 1.9,
    ppm: 265,
    costPerUnit: 140,
    energyPerUnit: 2.6,
    auditScore: 93
  }];
  // Top KPIs
  const bestOEE = Math.max(...plantData.map(p => p.oee));
  const worstOEE = Math.min(...plantData.map(p => p.oee));
  const bestScrap = Math.min(...plantData.map(p => p.scrapRate));
  const worstScrap = Math.max(...plantData.map(p => p.scrapRate));
  const bestPPM = Math.min(...plantData.map(p => p.ppm));
  const bestEnergy = Math.min(...plantData.map(p => p.energyPerUnit));
  const bestOEEPlant = plantData.find(p => p.oee === bestOEE)?.plant;
  const worstOEEPlant = plantData.find(p => p.oee === worstOEE)?.plant;
  const bestScrapPlant = plantData.find(p => p.scrapRate === bestScrap)?.plant;
  const worstScrapPlant = plantData.find(p => p.scrapRate === worstScrap)?.plant;
  const bestPPMPlant = plantData.find(p => p.ppm === bestPPM)?.plant;
  const bestEnergyPlant = plantData.find(p => p.energyPerUnit === bestEnergy)?.plant;
  // Chart data - sorted
  const oeeChartData = [...plantData].sort((a, b) => b.oee - a.oee);
  const scrapChartData = [...plantData].sort((a, b) => a.scrapRate - b.scrapRate);
  const ppmChartData = [...plantData];
  const costChartData = [...plantData];
  // Energy trend data (monthly)
  const energyTrendData = [{
    month: 'Jan',
    Manesar: 2.9,
    Chennai: 2.6,
    Pune: 3.2,
    Bangalore: 2.7
  }, {
    month: 'Feb',
    Manesar: 2.8,
    Chennai: 2.5,
    Pune: 3.1,
    Bangalore: 2.6
  }, {
    month: 'Mar',
    Manesar: 2.8,
    Chennai: 2.5,
    Pune: 3.1,
    Bangalore: 2.6
  }, {
    month: 'Apr',
    Manesar: 2.9,
    Chennai: 2.6,
    Pune: 3.2,
    Bangalore: 2.7
  }, {
    month: 'May',
    Manesar: 2.8,
    Chennai: 2.5,
    Pune: 3.0,
    Bangalore: 2.6
  }, {
    month: 'Jun',
    Manesar: 2.8,
    Chennai: 2.5,
    Pune: 3.1,
    Bangalore: 2.6
  }];
  // Top & Bottom Performers
  const topOEEPlant = plantData.reduce((prev, current) => prev.oee > current.oee ? prev : current);
  const highestScrapPlant = plantData.reduce((prev, current) => prev.scrapRate > current.scrapRate ? prev : current);
  const highestPPMPlant = plantData.reduce((prev, current) => prev.ppm > current.ppm ? prev : current);
  const mostEfficientPlant = plantData.reduce((prev, current) => prev.energyPerUnit < current.energyPerUnit ? prev : current);
  // Health status functions
  const getOEEStatus = (oee: number) => {
    if (oee > 70) return {
      color: 'bg-green-100 text-green-800',
      label: 'Good'
    };
    if (oee >= 55) return {
      color: 'bg-yellow-100 text-yellow-800',
      label: 'Warning'
    };
    return {
      color: 'bg-red-100 text-red-800',
      label: 'Critical'
    };
  };
  const getScrapStatus = (scrap: number) => {
    if (scrap < 2) return {
      color: 'bg-green-100 text-green-800',
      label: 'Good'
    };
    if (scrap <= 4) return {
      color: 'bg-yellow-100 text-yellow-800',
      label: 'Warning'
    };
    return {
      color: 'bg-red-100 text-red-800',
      label: 'Critical'
    };
  };
  const getPPMStatus = (ppm: number) => {
    if (ppm < 200) return {
      color: 'bg-green-100 text-green-800',
      label: 'Good'
    };
    if (ppm <= 500) return {
      color: 'bg-yellow-100 text-yellow-800',
      label: 'Warning'
    };
    return {
      color: 'bg-red-100 text-red-800',
      label: 'Critical'
    };
  };
  const getEnergyStatus = (energy: number) => {
    if (energy < 4) return {
      color: 'bg-green-100 text-green-800',
      label: 'Good'
    };
    if (energy <= 6) return {
      color: 'bg-yellow-100 text-yellow-800',
      label: 'Warning'
    };
    return {
      color: 'bg-red-100 text-red-800',
      label: 'Critical'
    };
  };
  const getAuditStatus = (audit: number) => {
    if (audit > 90) return {
      color: 'bg-green-100 text-green-800',
      label: 'Good'
    };
    if (audit >= 80) return {
      color: 'bg-yellow-100 text-yellow-800',
      label: 'Warning'
    };
    return {
      color: 'bg-red-100 text-red-800',
      label: 'Critical'
    };
  };
  return <div data-id="element-3059">
      <DescriptionPanel title="Plant Benchmarking" description="Comprehensive comparison of manufacturing plant performance across key operational metrics. Identify best practices, performance gaps, and improvement opportunities to drive operational excellence across the network." insights={[`Chennai plant leads with ${bestOEE}% OEE - 6.5% above network average`, `Pune plant has highest scrap rate at ${worstScrap}% - ₹12 Cr annual impact`, `${bestPPMPlant} achieves best quality with ${bestPPM} PPM defect rate`, `Energy efficiency varies by 24% across plants - standardization opportunity`]} data-id="element-3060" />
      {/* TOP SECTION - KPI Comparison Row */}
      <div className="mb-8" data-id="element-3061">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3062">
          Best & Worst Performance Indicators
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3063">
          <Card className="p-5 bg-gradient-to-br from-green-50 to-white border-2 border-green-200" data-id="element-3064">
            <div className="flex items-center justify-between mb-2" data-id="element-3065">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide" data-id="element-3066">
                Best OEE %
              </div>
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-3067" />
            </div>
            <div className="text-3xl font-bold text-green-700 mb-1" data-id="element-3068">
              {bestOEE}%
            </div>
            <div className="text-sm text-gray-600" data-id="element-3069">{bestOEEPlant} Plant</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-red-50 to-white border-2 border-red-200" data-id="element-3070">
            <div className="flex items-center justify-between mb-2" data-id="element-3071">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide" data-id="element-3072">
                Worst OEE %
              </div>
              <AlertTriangleIcon size={20} className="text-red-600" data-id="element-3073" />
            </div>
            <div className="text-3xl font-bold text-red-700 mb-1" data-id="element-3074">
              {worstOEE}%
            </div>
            <div className="text-sm text-gray-600" data-id="element-3075">{worstOEEPlant} Plant</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-green-50 to-white border-2 border-green-200" data-id="element-3076">
            <div className="flex items-center justify-between mb-2" data-id="element-3077">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide" data-id="element-3078">
                Best Scrap Rate %
              </div>
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-3079" />
            </div>
            <div className="text-3xl font-bold text-green-700 mb-1" data-id="element-3080">
              {bestScrap}%
            </div>
            <div className="text-sm text-gray-600" data-id="element-3081">{bestScrapPlant} Plant</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-red-50 to-white border-2 border-red-200" data-id="element-3082">
            <div className="flex items-center justify-between mb-2" data-id="element-3083">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide" data-id="element-3084">
                Worst Scrap Rate %
              </div>
              <AlertTriangleIcon size={20} className="text-red-600" data-id="element-3085" />
            </div>
            <div className="text-3xl font-bold text-red-700 mb-1" data-id="element-3086">
              {worstScrap}%
            </div>
            <div className="text-sm text-gray-600" data-id="element-3087">{worstScrapPlant} Plant</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-green-50 to-white border-2 border-green-200" data-id="element-3088">
            <div className="flex items-center justify-between mb-2" data-id="element-3089">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide" data-id="element-3090">
                Best PPM (Quality)
              </div>
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-3091" />
            </div>
            <div className="text-3xl font-bold text-green-700 mb-1" data-id="element-3092">
              {bestPPM}
            </div>
            <div className="text-sm text-gray-600" data-id="element-3093">{bestPPMPlant} Plant</div>
          </Card>
          <Card className="p-5 bg-gradient-to-br from-green-50 to-white border-2 border-green-200" data-id="element-3094">
            <div className="flex items-center justify-between mb-2" data-id="element-3095">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide" data-id="element-3096">
                Best Energy Efficiency
              </div>
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-3097" />
            </div>
            <div className="text-3xl font-bold text-green-700 mb-1" data-id="element-3098">
              {bestEnergy}
            </div>
            <div className="text-sm text-gray-600" data-id="element-3099">
              {bestEnergyPlant} Plant • kWh/Unit
            </div>
          </Card>
        </div>
      </div>
      {/* SECTION A - Plant Comparison Grid */}
      <Card className="p-6 mb-8" data-id="element-3100">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3101">
          Plant Comparison Matrix
        </h3>
        <div className="overflow-x-auto" data-id="element-3102">
          <table className="w-full text-sm" data-id="element-3103">
            <thead data-id="element-3104">
              <tr className="border-b-2 border-gray-300" data-id="element-3105">
                <th className="text-left py-3 px-4 font-bold text-gray-700" data-id="element-3106">
                  Plant Name
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3107">
                  OEE %
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3108">
                  Scrap Rate %
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3109">
                  PPM
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3110">
                  Cost per Unit (₹)
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3111">
                  Energy per Unit (kWh)
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3112">
              {plantData.map((plant, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-3113">
                  <td className="py-3 px-4 font-medium text-gray-900" data-id="element-3114">
                    {plant.plant}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3115">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.oee === bestOEE ? 'bg-green-100 text-green-700' : plant.oee === worstOEE ? 'bg-red-100 text-red-700' : 'text-gray-700'}`} data-id="element-3116">
                      {plant.oee}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3117">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.scrapRate === bestScrap ? 'bg-green-100 text-green-700' : plant.scrapRate === worstScrap ? 'bg-red-100 text-red-700' : 'text-gray-700'}`} data-id="element-3118">
                      {plant.scrapRate}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3119">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.ppm === bestPPM ? 'bg-green-100 text-green-700' : 'text-gray-700'}`} data-id="element-3120">
                      {plant.ppm}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center font-semibold text-gray-700" data-id="element-3121">
                    ₹{plant.costPerUnit}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3122">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.energyPerUnit === bestEnergy ? 'bg-green-100 text-green-700' : 'text-gray-700'}`} data-id="element-3123">
                      {plant.energyPerUnit}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </Card>
      {/* SECTION B - Visual Benchmarking Charts */}
      <div className="mb-8" data-id="element-3124">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3125">
          Visual Benchmarking
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-3126">
          {/* Chart 1 - OEE Comparison */}
          <Card className="p-6" data-id="element-3127">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3128">
              OEE Comparison by Plant
            </h3>
            <ResponsiveContainer width="100%" height={300} data-id="element-3129">
              <BarChart data={oeeChartData} data-id="element-3130">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-3131" />
                <XAxis dataKey="plant" tick={{
                fontSize: 12
              }} data-id="element-3132" />
                <YAxis tick={{
                fontSize: 12
              }} domain={[0, 100]} data-id="element-3133" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-3134" />
                <Bar dataKey="oee" fill="#3b82f6" name="OEE %" data-id="element-3135" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          {/* Chart 2 - Scrap Rate */}
          <Card className="p-6" data-id="element-3136">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3137">
              Scrap Rate by Plant
            </h3>
            <ResponsiveContainer width="100%" height={300} data-id="element-3138">
              <BarChart data={scrapChartData} layout="vertical" data-id="element-3139">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-3140" />
                <XAxis type="number" tick={{
                fontSize: 12
              }} data-id="element-3141" />
                <YAxis dataKey="plant" type="category" tick={{
                fontSize: 12
              }} data-id="element-3142" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-3143" />
                <Bar dataKey="scrapRate" fill="#ef4444" name="Scrap %" data-id="element-3144" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6" data-id="element-3145">
          {/* Chart 3 - PPM */}
          <Card className="p-6" data-id="element-3146">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3147">
              PPM by Plant
            </h3>
            <ResponsiveContainer width="100%" height={300} data-id="element-3148">
              <BarChart data={ppmChartData} data-id="element-3149">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-3150" />
                <XAxis dataKey="plant" tick={{
                fontSize: 12
              }} data-id="element-3151" />
                <YAxis tick={{
                fontSize: 12
              }} data-id="element-3152" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} data-id="element-3153" />
                <Bar dataKey="ppm" fill="#f59e0b" name="PPM" data-id="element-3154" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
          {/* Chart 4 - Cost per Unit */}
          <Card className="p-6" data-id="element-3155">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3156">
              Cost per Unit by Plant
            </h3>
            <ResponsiveContainer width="100%" height={300} data-id="element-3157">
              <BarChart data={costChartData} data-id="element-3158">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-3159" />
                <XAxis dataKey="plant" tick={{
                fontSize: 12
              }} data-id="element-3160" />
                <YAxis tick={{
                fontSize: 12
              }} data-id="element-3161" />
                <Tooltip contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px'
              }} formatter={value => `₹${value}`} data-id="element-3162" />
                <Bar dataKey="costPerUnit" fill="#8b5cf6" name="Cost per Unit (₹)" data-id="element-3163" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
        {/* Chart 5 - Energy Trend */}
        <Card className="p-6" data-id="element-3164">
          <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3165">
            Energy Consumption Trend by Plant
          </h3>
          <ResponsiveContainer width="100%" height={300} data-id="element-3166">
            <LineChart data={energyTrendData} data-id="element-3167">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" data-id="element-3168" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-3169" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-3170" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-3171" />
              <Legend data-id="element-3172" />
              <Line type="monotone" dataKey="Manesar" stroke="#3b82f6" strokeWidth={2} data-id="element-3173" />
              <Line type="monotone" dataKey="Chennai" stroke="#10b981" strokeWidth={2} data-id="element-3174" />
              <Line type="monotone" dataKey="Pune" stroke="#ef4444" strokeWidth={2} data-id="element-3175" />
              <Line type="monotone" dataKey="Bangalore" stroke="#f59e0b" strokeWidth={2} data-id="element-3176" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {/* SECTION C - Top & Bottom Performers */}
      <div className="mb-8" data-id="element-3177">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3178">
          Top & Bottom Performers
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3179">
          {/* Top Performer - OEE */}
          <Card className="p-6 bg-gradient-to-br from-green-500 to-emerald-600 text-white" data-id="element-3180">
            <div className="flex items-center justify-between mb-4" data-id="element-3181">
              <h4 className="text-lg font-bold" data-id="element-3182">Top Performer (OEE)</h4>
              <CheckCircleIcon size={24} data-id="element-3183" />
            </div>
            <div className="text-3xl font-bold mb-2" data-id="element-3184">{topOEEPlant.plant}</div>
            <div className="space-y-2 text-sm" data-id="element-3185">
              <div className="flex justify-between" data-id="element-3186">
                <span data-id="element-3187">OEE:</span>
                <span className="font-bold" data-id="element-3188">{topOEEPlant.oee}%</span>
              </div>
              <div className="flex justify-between" data-id="element-3189">
                <span data-id="element-3190">Scrap Rate:</span>
                <span className="font-bold" data-id="element-3191">{topOEEPlant.scrapRate}%</span>
              </div>
              <div className="flex justify-between" data-id="element-3192">
                <span data-id="element-3193">PPM:</span>
                <span className="font-bold" data-id="element-3194">{topOEEPlant.ppm}</span>
              </div>
            </div>
          </Card>
          {/* Highest Scrap Plant */}
          <Card className="p-6 bg-gradient-to-br from-red-500 to-rose-600 text-white" data-id="element-3195">
            <div className="flex items-center justify-between mb-4" data-id="element-3196">
              <h4 className="text-lg font-bold" data-id="element-3197">Highest Scrap Plant</h4>
              <AlertTriangleIcon size={24} data-id="element-3198" />
            </div>
            <div className="text-3xl font-bold mb-2" data-id="element-3199">
              {highestScrapPlant.plant}
            </div>
            <div className="space-y-2 text-sm" data-id="element-3200">
              <div className="flex justify-between" data-id="element-3201">
                <span data-id="element-3202">Scrap Rate:</span>
                <span className="font-bold" data-id="element-3203">
                  {highestScrapPlant.scrapRate}%
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3204">
                <span data-id="element-3205">Annual Cost Impact:</span>
                <span className="font-bold" data-id="element-3206">₹12 Cr</span>
              </div>
              <div className="flex justify-between" data-id="element-3207">
                <span data-id="element-3208">Improvement Potential:</span>
                <span className="font-bold" data-id="element-3209">0.6% reduction</span>
              </div>
            </div>
          </Card>
          {/* Lowest Quality (Highest PPM) */}
          <Card className="p-6 bg-gradient-to-br from-orange-500 to-amber-600 text-white" data-id="element-3210">
            <div className="flex items-center justify-between mb-4" data-id="element-3211">
              <h4 className="text-lg font-bold" data-id="element-3212">
                Lowest Quality (Highest PPM)
              </h4>
              <AlertTriangleIcon size={24} data-id="element-3213" />
            </div>
            <div className="text-3xl font-bold mb-2" data-id="element-3214">
              {highestPPMPlant.plant}
            </div>
            <div className="space-y-2 text-sm" data-id="element-3215">
              <div className="flex justify-between" data-id="element-3216">
                <span data-id="element-3217">PPM:</span>
                <span className="font-bold" data-id="element-3218">{highestPPMPlant.ppm}</span>
              </div>
              <div className="flex justify-between" data-id="element-3219">
                <span data-id="element-3220">Gap to Best:</span>
                <span className="font-bold" data-id="element-3221">
                  {highestPPMPlant.ppm - bestPPM} PPM
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3222">
                <span data-id="element-3223">Top Defect:</span>
                <span className="font-bold" data-id="element-3224">LED Driver Failure</span>
              </div>
            </div>
          </Card>
          {/* Most Efficient Plant (Energy) */}
          <Card className="p-6 bg-gradient-to-br from-teal-500 to-cyan-600 text-white" data-id="element-3225">
            <div className="flex items-center justify-between mb-4" data-id="element-3226">
              <h4 className="text-lg font-bold" data-id="element-3227">
                Most Efficient Plant (Energy)
              </h4>
              <CheckCircleIcon size={24} data-id="element-3228" />
            </div>
            <div className="text-3xl font-bold mb-2" data-id="element-3229">
              {mostEfficientPlant.plant}
            </div>
            <div className="space-y-2 text-sm" data-id="element-3230">
              <div className="flex justify-between" data-id="element-3231">
                <span data-id="element-3232">Energy per Unit:</span>
                <span className="font-bold" data-id="element-3233">
                  {mostEfficientPlant.energyPerUnit} kWh
                </span>
              </div>
              <div className="flex justify-between" data-id="element-3234">
                <span data-id="element-3235">vs Network Avg:</span>
                <span className="font-bold" data-id="element-3236">-12% lower</span>
              </div>
              <div className="flex justify-between" data-id="element-3237">
                <span data-id="element-3238">Annual Savings:</span>
                <span className="font-bold" data-id="element-3239">₹4.2 Cr</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {/* SECTION D - Plant Health Summary (Traffic Light) */}
      <Card className="p-6" data-id="element-3240">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-3241">
          Plant Health Summary (Traffic Light)
        </h3>
        <div className="overflow-x-auto" data-id="element-3242">
          <table className="w-full text-sm" data-id="element-3243">
            <thead data-id="element-3244">
              <tr className="border-b-2 border-gray-300" data-id="element-3245">
                <th className="text-left py-3 px-4 font-bold text-gray-700" data-id="element-3246">
                  Plant
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3247">
                  OEE
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3248">
                  Scrap
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3249">
                  PPM
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3250">
                  Energy/Unit
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-3251">
                  Audit Score
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3252">
              {plantData.map((plant, index) => {
              const oeeStatus = getOEEStatus(plant.oee);
              const scrapStatus = getScrapStatus(plant.scrapRate);
              const ppmStatus = getPPMStatus(plant.ppm);
              const energyStatus = getEnergyStatus(plant.energyPerUnit);
              const auditStatus = getAuditStatus(plant.auditScore);
              return <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-3253">
                    <td className="py-3 px-4 font-medium text-gray-900" data-id="element-3254">
                      {plant.plant}
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-3255">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${oeeStatus.color}`} data-id="element-3256">
                        {plant.oee}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-3257">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${scrapStatus.color}`} data-id="element-3258">
                        {plant.scrapRate}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-3259">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${ppmStatus.color}`} data-id="element-3260">
                        {plant.ppm}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-3261">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${energyStatus.color}`} data-id="element-3262">
                        {plant.energyPerUnit}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center" data-id="element-3263">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${auditStatus.color}`} data-id="element-3264">
                        {plant.auditScore}
                      </span>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3265">
          <div className="flex items-center space-x-6 text-xs" data-id="element-3266">
            <div className="flex items-center" data-id="element-3267">
              <div className="w-4 h-4 bg-green-100 rounded mr-2" data-id="element-3268" />
              <span className="font-semibold" data-id="element-3269">Good</span>
              <span className="text-gray-600 ml-1" data-id="element-3270">
                (OEE &gt;70%, Scrap &lt;2%, PPM &lt;200, Energy &lt;4, Audit
                &gt;90)
              </span>
            </div>
            <div className="flex items-center" data-id="element-3271">
              <div className="w-4 h-4 bg-yellow-100 rounded mr-2" data-id="element-3272" />
              <span className="font-semibold" data-id="element-3273">Warning</span>
              <span className="text-gray-600 ml-1" data-id="element-3274">
                (Mid-range performance)
              </span>
            </div>
            <div className="flex items-center" data-id="element-3275">
              <div className="w-4 h-4 bg-red-100 rounded mr-2" data-id="element-3276" />
              <span className="font-semibold" data-id="element-3277">Critical</span>
              <span className="text-gray-600 ml-1" data-id="element-3278">(Below threshold)</span>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default PlantBenchmarkingDashboard;